import * as React from 'react';
import moment from 'moment';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  CardActions,
  Grid,
  Paper,
  List,
  ListItem,
  Divider,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  Collapse,
  Checkbox,
  Badge,
  FormControlLabel,
  Button,
  Snackbar
} from '@material-ui/core';

import { CircularProgress } from '@material-ui/core';
import SatelliteIcon from '@material-ui/icons/Satellite';
import TodayIcon from '@material-ui/icons/Today';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { DatePicker } from 'material-ui-pickers';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import {
  FarmScenesProvider,
  SaveScenesProvider,
  SaveScenesParams,
  LayoutToolsProvider,
  LayoutTools
} from '../providers';

import { timestampToString } from '../utils';
import SidePanel from './SidePanel';

type Props = WithStyles & RouteComponentProps<any>;

interface State {
  dateMenuOpen: boolean;
  criticalMonthsOpen: boolean;
  selectedScenes: Scene[];
  spacecrafts: string[];
  capturedSince: Date | null;
  capturedUntil: Date | null;
  criticalMonths: number[];
}

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  loadingMore: boolean;
  anythingLoading: boolean;
  fetchMore: () => void;
  scenes: Scene[] | null;
  save: (scenes: SaveScenesParams) => void;
  saving: boolean;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  list: {
    marginBottom: 30
  },
  badge: {
    width: '100%'
  },
  content: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  card: {
    maxWidth: 345
  },
  media: {
    height: 150
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300
  }
});

const SceneCard: React.SFC<{
  cloudCoverage: number;
  satellite: string;
  date: string;
  thumbnailUrl?: string;
  classes: Record<string, string>;
  onSelect: () => void;
  onUnselect: () => void;
}> = props => (
  <Card className={props.classes.card}>
    {props.thumbnailUrl && (
      <CardMedia className={props.classes.media} image={props.thumbnailUrl} />
    )}
    <CardContent>
      <Typography variant="headline" component="h4">
        {props.date}
      </Typography>
      <Typography variant="subheading">
        Satélite: {props.satellite}
        <br />
        Nubes: {props.cloudCoverage}%
      </Typography>
    </CardContent>
    <CardActions>
      <FormControlLabel
        control={
          <Checkbox
            onChange={(evt, checked) =>
              checked ? props.onSelect() : props.onUnselect()
            }
          />
        }
        label="Seleccionar Escena"
      />
    </CardActions>
  </Card>
);

class ChooseScenes extends React.Component<Props, State> {
  state = {
    dateMenuOpen: false,
    criticalMonthsOpen: false,
    selectedScenes: [] as Scene[],
    spacecrafts: ['landsat8', 'landsat7', 'landsat5', 'sentinel2'],
    capturedSince: null,
    capturedUntil: null,
    criticalMonths: [] as number[]
  };

  componentDidMount() {
    window.scenesFlag = true;
    console.warn(window.scenesFlag);
  }

  handleToggleFormat(spacecraft: string) {
    if (this.state.spacecrafts.indexOf(spacecraft) > -1) {
      this.setState({
        spacecrafts: this.state.spacecrafts.filter(s => s !== spacecraft)
      });
    } else {
      this.setState({
        spacecrafts: [...this.state.spacecrafts, spacecraft]
      });
    }
  }

  handleToggleScene(scene: Scene) {
    if (this.state.selectedScenes.find(s => s.id === scene.id)) {
      this.setState({
        selectedScenes: this.state.selectedScenes.filter(f => f !== scene)
      });
    } else {
      this.setState({
        selectedScenes: [...this.state.selectedScenes, scene]
      });
    }
  }

  handleSaveScenes(execute: (variables: {}) => void) {
    const { selectedScenes } = this.state;
    execute({
      farmId: this.props.match.params.farmId,
      sceneIds: selectedScenes.map(s => s.id)
    });
  }

  handleToggleMonth(month: number) {
    if (this.state.criticalMonths.find(m => m === month)) {
      this.setState({
        criticalMonths: this.state.criticalMonths.filter(m => m !== month)
      });
    } else {
      this.setState({
        criticalMonths: [...this.state.criticalMonths, month]
      });
    }
  }

  renderMonthCheckbox(monthNumber: number, monthName: string) {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={this.state.criticalMonths.indexOf(monthNumber) > -1}
            onChange={() => this.handleToggleMonth(monthNumber)}
          />
        }
        label={monthName}
        style={{
          display: 'flex',
          flex: 1
        }}
      />
    );
  }

  renderScenes(scenes: Scene[]) {
    const { classes } = this.props;
    return scenes.map(scene => (
      <Grid key={scene.id} item={true} xs={12} sm={4}>
        <SceneCard
          cloudCoverage={scene.cloudCoverage}
          satellite={scene.spacecraft}
          date={timestampToString(scene.capturedOn)}
          classes={classes}
          onSelect={() => this.handleToggleScene(scene)}
          onUnselect={() => this.handleToggleScene(scene)}
        />
      </Grid>
    ));
  }

  renderPanelItems(renders: Renders) {
    const { classes } = this.props;
    return (
      <>
        <Typography variant="headline">Seleccionar Escenas</Typography>
        <Typography variant="subheading" paragraph={true}>
          Seleccione todas las escenas que sean relavantes para su lote y luego
          guardelas en su biblioteca para visualizarlas cuando lo necesite.
        </Typography>
        <List component="nav" className={classes.list}>
          <ListItem>
            <ListItemIcon>
              <SatelliteIcon />
            </ListItemIcon>
            <ListItemText primary="Landsat 8" />
            <ListItemSecondaryAction>
              <Switch
                checked={this.state.spacecrafts.indexOf('landsat8') > -1}
                onClick={() => this.handleToggleFormat('landsat8')}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <SatelliteIcon />
            </ListItemIcon>
            <ListItemText primary="Landsat 7" />
            <ListItemSecondaryAction>
              <Switch
                checked={this.state.spacecrafts.indexOf('landsat7') > -1}
                onClick={() => this.handleToggleFormat('landsat7')}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <SatelliteIcon />
            </ListItemIcon>
            <ListItemText primary="Landsat 5" />
            <ListItemSecondaryAction>
              <Switch
                checked={this.state.spacecrafts.indexOf('landsat5') > -1}
                onClick={() => this.handleToggleFormat('landsat5')}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <SatelliteIcon />
            </ListItemIcon>
            <ListItemText primary="Sentinel 2" />
            <ListItemSecondaryAction>
              <Switch
                checked={this.state.spacecrafts.indexOf('sentinel2') > -1}
                onClick={() => this.handleToggleFormat('sentinel2')}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <SatelliteIcon />
            </ListItemIcon>
            <ListItemText primary="Planet" />
            <ListItemSecondaryAction>
              <Switch
                checked={false}
                onClick={() => renders.layout.showBetaDialog()}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />

          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                dateMenuOpen: !this.state.dateMenuOpen
              })
            }
          >
            <ListItemIcon>
              <TodayIcon />
            </ListItemIcon>
            <ListItemText primary="Filtrar por Fecha" />
            {this.state.dateMenuOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.dateMenuOpen}>
            <List>
              <ListItem>
                <DatePicker
                  fullWidth={true}
                  label="Desde"
                  clearable={true}
                  disableFuture={true}
                  value={this.state.capturedSince}
                  onChange={d => this.setState({ capturedSince: d })}
                  maxDate={this.state.capturedUntil || undefined}
                  clearLabel="Limpiar"
                  labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                />
              </ListItem>
              <ListItem>
                <DatePicker
                  fullWidth={true}
                  label="Hasta"
                  clearable={true}
                  disableFuture={true}
                  value={this.state.capturedUntil}
                  onChange={d => this.setState({ capturedUntil: d })}
                  minDate={this.state.capturedSince || undefined}
                  clearLabel="Limpiar"
                  labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                />
              </ListItem>
            </List>
          </Collapse>
          <Divider />

          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                criticalMonthsOpen: !this.state.criticalMonthsOpen
              })
            }
          >
            <ListItemIcon>
              <TodayIcon />
            </ListItemIcon>
            <ListItemText primary="Meses críticos" />
            {this.state.criticalMonthsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.criticalMonthsOpen}>
            <List style={{ maxHeight: 250, overflowY: 'scroll' }}>
              <ListItem
                style={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                {this.renderMonthCheckbox(1, 'Enero')}
                {this.renderMonthCheckbox(2, 'Febrero')}
                {this.renderMonthCheckbox(3, 'Marzo')}
                {this.renderMonthCheckbox(4, 'Abril')}
                {this.renderMonthCheckbox(5, 'Mayo')}
                {this.renderMonthCheckbox(6, 'Junio')}
                {this.renderMonthCheckbox(7, 'Julio')}
                {this.renderMonthCheckbox(8, 'Agosto')}
                {this.renderMonthCheckbox(9, 'Septiembre')}
                {this.renderMonthCheckbox(10, 'Octubre')}
                {this.renderMonthCheckbox(11, 'Noviembre')}
                {this.renderMonthCheckbox(12, 'Diciembre')}
              </ListItem>
            </List>
          </Collapse>
        </List>

        <Badge
          color="secondary"
          badgeContent={this.state.selectedScenes.length}
          className={classes.badge}
        >
          <Button
            variant="raised"
            color="primary"
            fullWidth={true}
            disabled={this.state.selectedScenes.length === 0 || renders.loading}
            onClick={() => this.handleSaveScenes(renders.save)}
          >
            Guardar en Biblioteca
          </Button>
        </Badge>
      </>
    );
  }

  renderLoading(renders: Renders) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={renders.anythingLoading}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>cargando</span>
          </>
        }
      />
    );
  }

  renderFetchMoreButton(renders: Renders) {
    return (
      <Grid item={true} xs={12} style={{ textAlign: 'center', marginTop: 10 }}>
        <Button onClick={renders.fetchMore} disabled={renders.anythingLoading}>
          Cargar Más
        </Button>
      </Grid>
    );
  }

  composeProviders(
    children: (renders: Renders) => React.ReactNode
  ): React.ReactNode {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;
    const { capturedSince, capturedUntil, criticalMonths } = this.state;

    let sceneFilter = {
      spacecrafts: this.state.spacecrafts,
      limit: 18
    } as any;

    if (capturedSince) {
      sceneFilter.capturedSince = moment(capturedSince).format('x');
    }

    if (capturedUntil) {
      sceneFilter.capturedUntil = moment(capturedUntil).format('x');
    }

    if (criticalMonths.length) {
      sceneFilter.criticalMonths = criticalMonths;
    }

    return (
      <LayoutToolsProvider>
        {layout => (
          <FarmScenesProvider farmId={farmId} sceneFilter={sceneFilter}>
            {(farm, scenes, loading, fetchMore) => (
              <SaveScenesProvider
                onCompleted={() => this.props.history.push(`/farms/${farmId}`)}
              >
                {(save, saving) =>
                  children({
                    layout,
                    loading,
                    loadingMore: false,
                    fetchMore,
                    scenes,
                    saving,
                    save,
                    anythingLoading: loading || saving
                  })
                }
              </SaveScenesProvider>
            )}
          </FarmScenesProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  render() {
    const { classes } = this.props;
    return this.composeProviders(renders => (
      <main className={classes.root}>
        <SidePanel>{this.renderPanelItems(renders)}</SidePanel>
        <Paper square={true} elevation={1} className={classes.content}>
          <Grid container={true} spacing={24} style={{ maxWidth: 900 }}>
            {this.renderLoading(renders)}
            {renders.scenes && this.renderScenes(renders.scenes)}
            {renders.scenes && this.renderFetchMoreButton(renders)}
          </Grid>
        </Paper>
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(ChooseScenes));



// WEBPACK FOOTER //
// ./src/containers/ChooseScenes.tsx